import { create } from 'zustand'
import type { CalendarEvent, EventDealLink, EventLinkSummary, SlackActivitySummary } from '@/api/types'
import { presalesDashboardApi } from '@/api/client'
import { showToast } from './uiStore'

interface PresalesDashboardState {
  // Calendar data
  calendarEvents: CalendarEvent[]
  calendarLinks: EventDealLink[]
  calendarConfigured: boolean
  calendarLoading: boolean
  calendarError: string | null
  connectionStatus: { configured: boolean; all_calendars_accessible: boolean } | null

  // Event link summary
  eventLinkSummary: EventLinkSummary | null
  eventLinkSummaryLoading: boolean

  // Deals data
  deals: any[]
  dealsLoading: boolean

  // Slack data
  slackConfigured: boolean
  slackLoading: boolean
  slackError: string | null
  slackBotInfo: { bot_id: string; app_id: string; name: string } | null

  // Calendar actions
  fetchCalendarEvents: (params?: {
    startDate?: string
    endDate?: string
    unlinkedOnly?: boolean
  }) => Promise<void>
  linkEvent: (data: {
    event_id?: number
    google_event_id?: string
    google_calendar_id?: string
    deal_id: number | null
    category: string
    notes?: string
    linked_by?: string
    prospect_company?: string
    presales_attendees?: string[]
    presales_minutes?: number
    frankieone_attendee_count?: number
    frankieone_total_minutes?: number
  }) => Promise<void>
  unlinkEvent: (linkId: number) => Promise<void>
  reassignProspectEvents: (prospectCompany: string, dealId: number) => Promise<{ reassigned: number }>
  reviewSuggestion: (suggestionId: number, data: {
    status: 'accepted' | 'rejected'
    category?: string
    reviewed_by?: string
  }) => Promise<void>
  testCalendarConnection: () => Promise<{
    configured: boolean
    all_calendars_accessible: boolean
  }>
  fetchDeals: () => Promise<void>
  fetchEventLinkSummary: () => Promise<void>

  // Slack actions
  testSlackConnection: () => Promise<void>
  getSlackActivity: (dealId: number) => Promise<{
    internal: SlackActivitySummary | null
    external: SlackActivitySummary | null
  } | null>
  syncSlackActivity: (dealId: number) => Promise<void>

  // Reset
  reset: () => void
}

const initialState = {
  calendarEvents: [],
  calendarLinks: [],
  calendarConfigured: false,
  calendarLoading: false,
  calendarError: null,
  connectionStatus: null,
  eventLinkSummary: null,
  eventLinkSummaryLoading: false,
  deals: [],
  dealsLoading: false,
  slackConfigured: false,
  slackLoading: false,
  slackError: null,
  slackBotInfo: null,
}

export const usePresalesDashboardStore = create<PresalesDashboardState>((set, get) => ({
  ...initialState,

  /**
   * Fetch calendar events from the API
   */
  fetchCalendarEvents: async (params) => {
    set({ calendarLoading: true, calendarError: null })

    try {
      const response = await presalesDashboardApi.getCalendarEvents(params)

      if (response.success && response.data) {
        set({
          calendarEvents: response.data.events,
          calendarLinks: response.data.links,
          calendarConfigured: response.data.configured,
          calendarLoading: false,
        })
      } else {
        set({
          calendarError: response.error || 'Failed to fetch calendar events',
          calendarLoading: false,
        })
        showToast(response.error || 'Failed to fetch calendar events', 'error')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      set({ calendarError: message, calendarLoading: false })
      showToast(`Error fetching calendar events: ${message}`, 'error')
    }
  },

  /**
   * Link a calendar event to a deal
   */
  linkEvent: async (data) => {
    set({ calendarError: null })

    try {
      const response = await presalesDashboardApi.linkEvent(data)

      if (response.success && response.data) {
        const msg = data.deal_id
          ? `Event linked to deal successfully. Updated ${response.data.hours_updated} presales hours.`
          : `Event tagged as early deal (${data.prospect_company}).`
        showToast(msg, 'success')
      } else {
        set({
          calendarError: response.error || 'Failed to link event',
        })
        showToast(response.error || 'Failed to link event', 'error')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      set({ calendarError: message })
      showToast(`Error linking event: ${message}`, 'error')
    }
  },

  /**
   * Unlink a calendar event from a deal
   */
  unlinkEvent: async (linkId) => {
    set({ calendarError: null })

    try {
      const response = await presalesDashboardApi.unlinkEvent(linkId)

      if (response.success && response.data) {
        showToast(`Event unlinked successfully. Updated ${response.data.hours_updated} presales hours.`, 'success')
      } else {
        set({
          calendarError: response.error || 'Failed to unlink event',
        })
        showToast(response.error || 'Failed to unlink event', 'error')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      set({ calendarError: message })
      showToast(`Error unlinking event: ${message}`, 'error')
    }
  },

  /**
   * Reassign all prospect events for a company to a real deal
   */
  reassignProspectEvents: async (prospectCompany, dealId) => {
    set({ calendarError: null })

    try {
      const response = await presalesDashboardApi.reassignBulkProspectEvents(prospectCompany, dealId)

      if (response.success && response.data) {
        showToast(`Reassigned ${response.data.reassigned} events to deal. Updated ${response.data.hours_updated} presales hours.`, 'success')
        // Refresh the summary to reflect changes
        await get().fetchEventLinkSummary()
        return { reassigned: response.data.reassigned }
      } else {
        set({ calendarError: response.error || 'Failed to reassign events' })
        showToast(response.error || 'Failed to reassign events', 'error')
        return { reassigned: 0 }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      set({ calendarError: message })
      showToast(`Error reassigning events: ${message}`, 'error')
      return { reassigned: 0 }
    }
  },

  /**
   * Review a suggestion (accept or reject)
   */
  reviewSuggestion: async (suggestionId, data) => {
    set({ calendarLoading: true, calendarError: null })

    try {
      const response = await presalesDashboardApi.reviewSuggestion(suggestionId, data)

      if (response.success && response.data) {
        showToast(`Suggestion ${data.status}. ${response.data.hours_updated > 0 ? `Updated ${response.data.hours_updated} presales hours.` : ''}`, 'success')

        // Refresh calendar events to update suggestions and links
        await get().fetchCalendarEvents()
      } else {
        set({
          calendarError: response.error || 'Failed to review suggestion',
          calendarLoading: false,
        })
        showToast(response.error || 'Failed to review suggestion', 'error')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      set({ calendarError: message, calendarLoading: false })
      showToast(`Error reviewing suggestion: ${message}`, 'error')
    }
  },

  /**
   * Test calendar connection status
   */
  testCalendarConnection: async () => {
    try {
      set({ calendarLoading: true, calendarError: null })

      const response = await presalesDashboardApi.testCalendarConnection()

      if (response.success && response.data) {
        const status = {
          configured: response.data.configured,
          all_calendars_accessible: response.data.all_calendars_accessible,
        }
        set({
          calendarConfigured: response.data.configured,
          connectionStatus: status,
          calendarLoading: false,
        })
        return status
      }

      const failStatus = { configured: false, all_calendars_accessible: false }
      set({ connectionStatus: failStatus, calendarLoading: false })
      return failStatus

    } catch (error) {
      console.error('Error testing calendar connection:', error)
      const message = error instanceof Error ? error.message : 'Unknown error'
      const failStatus = { configured: false, all_calendars_accessible: false }
      set({
        connectionStatus: failStatus,
        calendarLoading: false,
        calendarError: `Connection test failed: ${message}`,
      })
      return failStatus
    }
  },

  /**
   * Fetch presales deals
   */
  fetchDeals: async () => {
    set({ dealsLoading: true })

    try {
      const response = await presalesDashboardApi.getDeals()

      if (response.success && response.data) {
        set({ deals: response.data.items || [], dealsLoading: false })
      } else {
        set({ deals: [], dealsLoading: false })
      }
    } catch (error) {
      console.error('Error fetching deals:', error)
      set({ deals: [], dealsLoading: false })
    }
  },

  /**
   * Fetch event link time tracking summary
   */
  fetchEventLinkSummary: async () => {
    set({ eventLinkSummaryLoading: true })
    try {
      const response = await presalesDashboardApi.getEventLinkSummary()
      if (response.success && response.data) {
        set({ eventLinkSummary: response.data, eventLinkSummaryLoading: false })
      } else {
        set({ eventLinkSummaryLoading: false })
      }
    } catch (error) {
      console.error('Error fetching event link summary:', error)
      set({ eventLinkSummaryLoading: false })
    }
  },

  /**
   * Test Slack bot connection
   */
  testSlackConnection: async () => {
    set({ slackLoading: true, slackError: null })

    try {
      const response = await presalesDashboardApi.testSlackConnection()

      if (response.success && response.data) {
        set({
          slackConfigured: response.data.configured,
          slackBotInfo: response.data.bot_info || null,
          slackError: response.data.error || null,
          slackLoading: false,
        })
      } else {
        set({
          slackConfigured: false,
          slackError: response.error || 'Failed to test Slack connection',
          slackLoading: false,
        })
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      set({ slackError: message, slackLoading: false, slackConfigured: false })
    }
  },

  /**
   * Get Slack activity for a deal
   */
  getSlackActivity: async (dealId: number) => {
    try {
      const response = await presalesDashboardApi.getSlackActivity(dealId)

      if (response.success && response.data) {
        return {
          internal: response.data.internal,
          external: response.data.external,
        }
      }

      return null
    } catch (error) {
      console.error('Error fetching Slack activity:', error)
      return null
    }
  },

  /**
   * Sync Slack activity for a deal
   */
  syncSlackActivity: async (dealId: number) => {
    set({ slackLoading: true, slackError: null })

    try {
      const response = await presalesDashboardApi.syncSlackActivity(dealId)

      if (response.success && response.data) {
        set({ slackLoading: false })

        if (response.data.errors && response.data.errors.length > 0) {
          showToast(`Synced ${response.data.synced} channels with ${response.data.errors.length} errors`, 'warning')
        } else {
          showToast(`Synced ${response.data.synced} Slack channels successfully`, 'success')
        }
      } else {
        set({
          slackError: response.error || 'Failed to sync Slack activity',
          slackLoading: false,
        })
        showToast(response.error || 'Failed to sync Slack activity', 'error')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      set({ slackError: message, slackLoading: false })
      showToast(`Error syncing Slack: ${message}`, 'error')
    }
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState)
  },
}))
